import {
    AfterViewInit,
    Component,
    Input,
    ViewEncapsulation
} from '@angular/core';
import { SubscriptionModel } from '../../shared/models/payment/subscription/subscription.model';
import { FacebookService } from '../../shared/services/facebook.service';
import {
    GoogleGtagService,
    PurchaseItem
} from '../../shared/services/google.gtag.service';
import { GooglePatternNumberFormatter } from '../../shared/services/google-pattern-number-formatter';

@Component({
    selector: 'completed-subscription',
    templateUrl: './completed.subscription.component.html',
    styleUrls: ['./../completed.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CompletedSubscriptionComponent implements AfterViewInit {
    @Input('subscription') subscription: SubscriptionModel;
    @Input('storeConfigurations') storeConfigurations = [];

    constructor(
        protected facebookService: FacebookService,
        protected googleGtagService: GoogleGtagService,
        protected googlePatternNumberFormatter: GooglePatternNumberFormatter
    ) { }

    ngAfterViewInit () {
        this.publishPurchaseValues();

        this.facebookService.configure(this.storeConfigurations);
        this.facebookService.trackPageView();
        this.facebookService.trackConversion(
            this.subscription.total,
            [this.subscription.plan.id],
            this.facebookService.CONTENT_TYPE_PRODUCT,
            this.subscription.plan.description,
            [{id: `${this.subscription.plan.id}`, quantity: 1}],
            1
        );

        const hasGoogleConversionId = this.storeConfigurations['google-conversion-id'] !== undefined
            && this.storeConfigurations['google-conversion-id'] !== null;

        const googleAnalyticsAccount = (this.storeConfigurations['google-analytics-account'] !== undefined
            && this.storeConfigurations['google-analytics-account'] !== null) ? this.storeConfigurations['google-analytics-account'] : null;

        this.googleGtagService
            .setHasGoogleConversionId(hasGoogleConversionId)
            .setGoogleConversionAccount(googleAnalyticsAccount)
            .purchase(
                `${this.subscription.id}`,
                this.subscription.total,
                '0',
                this.subscription.freight,
                this.buildItems()
            );
    }

    private buildItems(): Array<PurchaseItem> {
        return [
            {
                id: `${this.subscription.plan.id}`,
                name: this.subscription.plan.description,
                category: 'Plan',
                quantity: 1,
                price: this.googlePatternNumberFormatter.transform(this.subscription.plan.amount),
                unit_price: this.googlePatternNumberFormatter.transform(this.subscription.plan.amount)
            }
        ];
    }

    public hasDeliveryAddress() {
        return this.subscription.deliveries !== undefined
            && this.subscription.deliveries.address !== undefined;
    }

    public getOrdersTotal(){
	    if (this.shouldUsePaymentScreenValues()) {
        	return (this.subscription as any).paymentScreenValues.total;
      	}

        if (!this.subscription || !this.subscription.orders || !this.subscription.orders.length) {
          return this.subscription.total;
        }

        if(this.subscription.orders.length > 1){
            return this.subscription.total;
        }

        if(parseFloat(this.subscription.orders[0].total) > parseFloat(this.subscription.total)){
            return this.subscription.orders[0].total;
        }

        return this.subscription.total;
    }

    public getOrdersSubTotal(){
		if (this.shouldUsePaymentScreenValues()) {
        	return (this.subscription as any).paymentScreenValues.subtotal;
      	}

        if (!this.subscription || !this.subscription.orders || !this.subscription.orders.length) {
          return this.subscription.plan.amount;
        }

        if(this.subscription.orders.length > 1){
            return this.subscription.plan.amount;
        }

		let subtotal = 0;
		let is_valid = false;

		if (
		    this.subscription
		    && Array.isArray(this.subscription.orders)
		    && this.subscription.orders.length > 0
		    && this.subscription.orders[0]
		    && Array.isArray(this.subscription.orders[0].items)
		    && this.subscription.orders[0].items.length > 0
		) {
			is_valid = true;

		    this.subscription.orders[0].items.forEach(function (item) {
		        if (!is_valid) {
		            return;
		        }

		        if (
		            !item
		            || item.unit_price == null
		            || item.quantity == null
		            || isNaN(parseFloat(String(item.unit_price)))
		            || isNaN(parseFloat(String(item.quantity)))
		        ) {
		            is_valid = false;
		            return;
		        }

		        let price = parseFloat(String(item.unit_price));
		        let quantity = parseFloat(String(item.quantity));

		        subtotal += price * quantity;
		    });

			if(is_valid) {
				return subtotal;
			}
		}

		if (parseFloat(this.subscription.orders[0].subtotal) > parseFloat(String(this.subscription.plan.amount))
			&& !is_valid
		){
            return this.subscription.orders[0].subtotal;
        }

        return this.subscription.total;
    }

    private shouldUsePaymentScreenValues(): boolean {
        const plan = this.subscription && this.subscription.plan as any;
        const pv   = this.subscription && (this.subscription as any).paymentScreenValues

        return !!(
            plan
            && plan.model_plan_id != null
            && plan.billing_cycles_to_renew > 1
            // && plan.freight_multiplication_factor > 1
            && pv
            && parseFloat(String(pv.total)) !== 0
            && parseFloat(String(pv.subtotal)) !== 0
        );
    }

    public getDisplayDiscount(): any {
        if (this.shouldUsePaymentScreenValues()) {
            return (this.subscription as any).paymentScreenValues.discount;
        }

        return this.subscription.discount;
    }

    public getDisplayFreight(): any {
        if (this.shouldUsePaymentScreenValues()) {
            return (this.subscription as any).paymentScreenValues.freight;
        }

        return this.subscription.freight;
    }

    private getInstallmentsCount(): number | null {
        const sub = this.subscription as any;

        return (this.subscription.payment_card && this.subscription.payment_card.installments)
            || sub.installments
            || (sub.orders && sub.orders[0] && sub.orders[0].installments)
            || (sub.payment && sub.payment.installments)
            || 1;
    }

    /**
     * Publishes a curated subscription/orders/plan/client/paymentScreenValues/
     * installment snapshot to window.finished_page_values_modifier, so an
     * external script can read them without touching this app's code.
     */
    private publishPurchaseValues(): void {
        const w = window as any;

        const sub = this.subscription as any;
        const plan = sub.plan as any;

        w.finished_page_values_modifier = {
            type: 'subscription',
            subscription: {
                id: this.subscription.id,
                auto_renew: sub.auto_renew,
                discount: this.subscription.discount,
                freight: this.subscription.freight,
                total: this.subscription.total,
                start_at: this.subscription.start_at,
                status: this.subscription.status
            },
            orders: (this.subscription.orders || []).map(order => ({
                id: order.id,
                discount: order.discount,
                freight: order.freight,
                subtotal: order.subtotal,
                total: order.total,
                paymentScreenValues: (order as any).paymentScreenValues
            })),
            plan: {
                id: plan.id,
                amount: plan.amount,
                description: plan.description,
                billing_cycles_to_renew: plan.billing_cycles_to_renew,
                fixed_amount: plan.fixed_amount,
                freight_multiplication_factor: plan.freight_multiplication_factor,
                model_plan_id: plan.model_plan_id,
                recurrency_model: plan.recurrency_model
            },
            client: this.subscription.client,
            paymentScreenValues: sub.paymentScreenValues,
            installment: this.getInstallmentsCount()
        };
    }

}



// WEBPACK FOOTER //
// /codebuild/output/src3048926480/src/src/app/completed/subscription/completed.subscription.component.ts